import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, Send, X, Bot, User, Minimize2 } from 'lucide-react';
import api from '../services/api';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const [sessionId, setSessionId] = useState(null);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Get welcome message from API
      const initializeChat = async () => {
        try {
          const response = await api.sendChatMessage('', null, null);
          setSessionId(response.session_id);
          setMessages([
            {
              id: 1,
              type: 'bot',
              message: response.response,
              timestamp: new Date()
            }
          ]);
        } catch (error) {
          console.error('Error initializing chat:', error);
          // Fallback message
          setMessages([
            {
              id: 1,
              type: 'bot',
              message: "Bonjour ! Je suis l'assistant Good-Bloom. Comment puis-je vous aider ?",
              timestamp: new Date()
            }
          ]);
        }
      };
      
      initializeChat();
    }
  }, [isOpen, messages.length]);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: message.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Call AI chatbot API
      const response = await api.sendChatMessage(message, sessionId);
      
      // Update session ID if new
      if (response.session_id && !sessionId) {
        setSessionId(response.session_id);
      }
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        message: response.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        message: "Désolé, je rencontre une difficulté technique. Un expert va vous recontacter rapidement.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickReply = (reply) => {
    handleSendMessage(reply);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendMessage();
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-full shadow-2xl z-40 transition-all duration-300 transform hover:scale-110 group animate-pulse"
      >
        <MessageCircle className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" />
      </Button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <Card className={`w-80 sm:w-96 bg-white shadow-2xl border-0 transition-all duration-300 ${
        isMinimized ? 'h-16' : 'h-96'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <div className="font-semibold">Assistant Good-Bloom</div>
              <div className="text-xs text-white/80">
                {isTyping ? 'En train d\'écrire...' : 'En ligne'}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-white hover:bg-white/20 p-1 h-8 w-8"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 p-1 h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <CardContent className="p-0 flex flex-col h-80">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start space-x-2 max-w-[80%] ${
                    msg.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      msg.type === 'user' 
                        ? 'bg-teal-500 text-white' 
                        : 'bg-white border-2 border-gray-200'
                    }`}>
                      {msg.type === 'user' ? (
                        <User className="w-4 h-4" />
                      ) : (
                        <Bot className="w-4 h-4 text-teal-500" />
                      )}
                    </div>
                    <div className={`p-3 rounded-2xl ${
                      msg.type === 'user'
                        ? 'bg-teal-500 text-white rounded-br-sm'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                    }`}>
                      <p className="text-sm leading-relaxed">{msg.message}</p>
                      <div className={`text-xs mt-1 ${
                        msg.type === 'user' ? 'text-white/70' : 'text-gray-500'
                      }`}>
                        {msg.timestamp.toLocaleTimeString('fr-FR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-2">
                    <div className="w-8 h-8 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-teal-500" />
                    </div>
                    <div className="bg-white border border-gray-200 p-3 rounded-2xl rounded-bl-sm">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick replies */}
              {messages.length === 1 && !isTyping && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-500 text-center">Suggestions rapides :</div>
                  <div className="flex flex-wrap gap-2">
                    {["Demander un devis", "Prendre rendez-vous", "En savoir plus sur vos technologies", "Voir vos réalisations"].map((reply, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickReply(reply)}
                        className="text-xs bg-white hover:bg-teal-50 hover:border-teal-300 border-gray-200"
                      >
                        {reply}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <form onSubmit={handleSubmit} className="flex space-x-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Tapez votre message..."
                  className="flex-1 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                  disabled={isTyping}
                />
                <Button
                  type="submit"
                  disabled={!inputMessage.trim() || isTyping}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ChatBot;